import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { dropTask, type Task } from 'ember-concurrency';
import config from 'garaje/config/environment';
import type Location from 'garaje/models/location';
import type { Watchlist } from 'garaje/models/location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import updateAndSaveTask from 'garaje/utils/decorators/update-and-save-task';
import WatchlistValidations from 'garaje/validations/watchlist';

type WatchlistChangeset = DetailedChangeset<Watchlist>;

interface WatchListComponentSignature {
  Args: {
    /** Whether the user can access watchlist features */
    canAccessWatchList: boolean;
    /** The location model containing watchlist data */
    location: Location;
    /** Optional callback for upgrading watchlist functionality */
    onUpgradeWatchlist?: () => void;
  };
}

/**
 * Security Watch List Component
 *
 * Manages watchlist settings for a location, allowing users to configure
 * email notifications and maintain a list of people to watch for.
 */
@updateAndSaveTask
export default class WatchListComponent extends Component<WatchListComponentSignature> {
  // Declare the updateAndSaveTask property added by the decorator
  declare updateAndSaveTask: Task<void, [Location]>;

  defaultCountry = config.defaultCountry;

  @service declare abilities: AbilitiesService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;

  @tracked isEditingWatchlist = false;
  @tracked watchlist: WatchlistChangeset;

  constructor(owner: unknown, args: WatchListComponentSignature['Args']) {
    super(owner, args);

    this.watchlist = this.createChangeset();
  }

  get canCreateWatchlist(): boolean {
    return this.abilities.can('create watchlist in security');
  }

  get canReadWatchlist(): boolean {
    return this.abilities.can('read watchlist in security');
  }

  get canUpdateWatchlist(): boolean {
    return this.abilities.can('update watchlist in security');
  }

  get isWatchlistEditableAndFulfilled(): boolean {
    return Boolean(this.args.location.watchlist.list);
  }

  get isInputDisabled(): boolean {
    return !this.canUpdateWatchlist;
  }

  createChangeset(): WatchlistChangeset {
    const watchlist = { ...this.args.location.watchlist };
    const validator = lookupValidator(WatchlistValidations);

    return Changeset(watchlist, validator, WatchlistValidations);
  }

  @action
  setupChangeset(): void {
    this.watchlist = this.createChangeset();
  }

  @action
  editWatchlist(): void {
    this.isEditingWatchlist = true;
    this.metrics.trackEvent('Security - Watchlist Expanded', { button_text: 'Edit' });
  }

  @action
  enableWatchlist(): void {
    this.isEditingWatchlist = true;
    this.metrics.trackEvent('Security - Watchlist Enable Requested');
    this.metrics.trackEvent('Security - Watchlist Expanded', { button_text: 'Enable' });
  }

  @action
  rollbackWatchlist(): void {
    this.isEditingWatchlist = false;
    this.args.location.rollbackAttributes();
  }

  @action
  updateList(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.watchlist.list = target.value;
  }

  saveWatchlist = dropTask(async (): Promise<void> => {
    const changeset = this.watchlist;

    try {
      this.metrics.trackEvent('Security - Watch List Settings Saved', { button_text: 'Save' });

      await changeset.validate();

      if (!changeset.isValid) {
        throw new Error('Please correct the form errors.');
      }

      // We can't proxy .save to the object since it doesn't support it.
      changeset.execute();
      changeset.rollback();

      // save location model
      const { location } = this.args;
      location.watchlist = Object.freeze(changeset.data);

      await this.updateAndSaveTask.perform(location);

      this.isEditingWatchlist = false;
    } catch (_) {
      const message_title = 'Please correct the form errors.';

      this.flashMessages.showAndHideFlash('error', message_title);
      this.metrics.trackEvent('Viewed Flash Message', {
        type: 'error',
        message_title,
        message_codes: 'error_watch_list_update',
        message_code_type: 'watch_list',
      });
    }
  });
}
